import { getDashboardById } from '@src/api/dashboards';
import { useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import {
  SAVED_DASHBOARDS_BASE_KEY,
  SAVED_DASHBOARDS_STATE_KEY,
  DASHBOARD_TYPE,
  isAppModule,
} from '@utils/constants';
import { SavedDataInterface } from './useSaveSet';
import useInitSave from './useInitSave';
import { useDispatch } from 'react-redux';
import { replace } from 'connected-react-router';
import { get, set, isNumber } from 'lodash';
import { getRemoteFilters, mergeWithRemoteFilters, type Filter } from '@utils/filter';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { initialFilter } from '@store/common/reducer';

const getFids = (filters: Filter[]) =>
  (filters || []).map((it) => it?.fid).filter(isNumber) as number[];

const updateFiltersForTrends = async (dashboardInfo: Record<string, any>) => {
  const appliedFiltersPath = 'config.state.common.appliedFilters';
  const summaryDrilldownDataPath = 'config.state.summary.drilldownData';
  const compareFiltersOnTrendsPath = 'config.state.module:trends.base.compareFilters';

  const filters = get(dashboardInfo, appliedFiltersPath, []);
  const compareFilters = get(dashboardInfo, compareFiltersOnTrendsPath, []);

  // collect all the filter id to get remote filter one time.
  const filterIds = getFids([
    ...(filters?.length ? filters : []),
    ...(compareFilters?.length ? compareFilters : []),
  ]);
  const remoteFilters = await getRemoteFilters(filterIds);

  // update compare filters
  const shouldUpdateCompareFilters = Array.isArray(compareFilters) && compareFilters.length;
  if (shouldUpdateCompareFilters) {
    set(
      dashboardInfo,
      compareFiltersOnTrendsPath,
      mergeWithRemoteFilters(compareFilters, remoteFilters),
    );
  }

  // Only update applied filter and drilldown filter when current applied filter is saved filter
  const shouldUpdateAppliedFilter = Boolean(filters?.length === 1 && isNumber(filters[0].fid));
  if (shouldUpdateAppliedFilter) {
    const newAppliedFilters = mergeWithRemoteFilters(filters, remoteFilters);
    // update applied filter
    set(dashboardInfo, appliedFiltersPath, newAppliedFilters);

    // update drilldown filter
    const drilldownFilters = newAppliedFilters.map((it) => {
      const tRules = getRulesFromFilter(it.rule, true);
      const tFilter = getFilterFromRules(tRules, true);
      return {
        name: it.name || tFilter.name,
        rule: tFilter.rule,
      };
    });
    set(dashboardInfo, summaryDrilldownDataPath, [initialFilter, ...drilldownFilters]);
  }
};

const updateFiltersForNOC = async (dashboardInfo: Record<string, any>) => {
  const appliedFilterPath = 'config.base.filters.appliedFilter';
  const trendsAppliedFiltersPath = 'config.base.trends.appliedFilter';

  const appliedFilter = get(dashboardInfo, appliedFilterPath, []);
  const trendsFilters = get(dashboardInfo, trendsAppliedFiltersPath, []);

  // collect all the filter id to get remote filter one time.
  const filterIds = getFids([
    ...(appliedFilter?.length ? appliedFilter : []),
    ...(trendsFilters?.length ? trendsFilters : []),
  ]);

  const remoteFilters = await getRemoteFilters(filterIds);

  const shouldUpdateAppliedFilter = Array.isArray(appliedFilter) && appliedFilter.length;
  if (shouldUpdateAppliedFilter) {
    set(dashboardInfo, appliedFilterPath, mergeWithRemoteFilters(appliedFilter, remoteFilters));
  }

  const shouldUpdateTrendsFilters = Array.isArray(trendsFilters) && trendsFilters.length;
  if (shouldUpdateTrendsFilters) {
    set(
      dashboardInfo,
      trendsAppliedFiltersPath,
      mergeWithRemoteFilters(trendsFilters, remoteFilters),
    );
  }
};

const useGetSavedDashboard = (dashboardId, pageKeyForSaveService, applySavedData) => {
  const [isSavedDataApplied, setIsSavedDataApplied] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { baseUrl } = window.conviva;

  useInitSave(pageKeyForSaveService);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [SAVED_DASHBOARDS_BASE_KEY, dashboardId],
    queryFn: async () => {
      const { data: dashboardInfo } = await getDashboardById(dashboardId);

      try {
        const dashboardType = get(dashboardInfo, 'dashboardType.id');
        if ([DASHBOARD_TYPE.TRENDS, DASHBOARD_TYPE.APP_EXPERIENCE].includes(dashboardType)) {
          await updateFiltersForTrends(dashboardInfo);
        } else if (dashboardType === DASHBOARD_TYPE.NOC) {
          await updateFiltersForNOC(dashboardInfo);
        }
      } catch (e) {
        /* do nothing */
      }
      return dashboardInfo;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    onSuccess: (data) => {
      const { id, name, isPublic, creatorId } = data;

      applySavedData(data);
      setIsSavedDataApplied(true);

      const currentSaveData: SavedDataInterface = queryClient.getQueryData<any>([
        SAVED_DASHBOARDS_STATE_KEY,
        pageKeyForSaveService,
      ]);

      currentSaveData.meta = {
        id,
        name,
        isPublic,
        creatorId,
        isSaved: true,
        hasChanged: false,
      };

      currentSaveData.initialSavedData = data;

      queryClient.setQueryData(
        [SAVED_DASHBOARDS_STATE_KEY, pageKeyForSaveService],
        currentSaveData,
      );
    },
    onError: () => {
      setIsSavedDataApplied(false);
      if (isAppModule) {
        dispatch(replace(`${baseUrl}/dashboard/app-experience`));
      } else {
        dispatch(replace(`${baseUrl}/dashboard/trends`));
      }
    },
  });

  return { isLoading, isError, savedData: data, refetch, isSavedDataApplied };
};

export default useGetSavedDashboard;
